import { Title as DialogTitle } from '@radix-ui/react-dialog';
import { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Callout } from '@/components/core/Callout';
import { Checkbox } from '@/components/core/Checkbox';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
} from '@/components/core/Dialog';
import { Grid, GridRef } from '@/components/core/Grid';
import { PaginationPage } from '@/components/core/PaginationPage/PaginationPage';
import { ToolbarTable } from '@/components/core/ToolbarTable';
import { isItemNamespaceReadonly, useOrganization } from '@/hooks/useOrganization';
import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { logger } from '@/services/logger';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

import {
    useBulkSubscriptionEntries,
    useBulkSubscriptionEntriesCount,
    useExportBulkSubscriptionEntries,
} from '../bulk-subscriptions.queries';
import { BulkSubscription, BulkSubscriptionEntry, BulkSubscriptionStatus } from '../bulk-subscriptions.types';
import { BulkSubDialogHeader, BulkSubDownloadFormat } from './BulkSubDialogHeader';
import { DeleteSelectedEntriesConfirmationDialog } from './DeleteSelectedEntriesConfirmationDialog';
import { DynamicEntryHeader } from './DynamicEntryHeader';
import { EntriesToolbar } from './EntriesToolbar';
import { UpdateDetailsDialog } from './UpdateDetailsDialog';
import { UploadNewEntriesDialog } from './UploadNewEntriesDialog';

interface UpdateBulkSubscriptionDialogProps {
    open: boolean;
    entity: BulkSubscription;
    onClose: () => void;
}

interface ColumnsProps {
    attributes: string[];
    globalSelectMode: boolean;
    isSelectionDisabled: boolean;
}

const ENTRY_COLUMN_MIN_SIZE = 150;
const SELECT_COLUMN_SIZE = 40;
// Horizontal breathing room the dialog reserves around the Grid: outer
// `!tw-w-[80vw]` on the DialogContent, then `tw-px-5 lg:tw-px-8` on the
// DialogBody (max 32px per side).
const DIALOG_HORIZONTAL_PADDING = 64;

// Nudges ToolbarTable's default `tw-bottom-6` (24px) up so the toolbar
// initially sits 80px above the DialogContent bottom edge (Figma spec).
const TOOLBAR_DEFAULT_POSITION = { x: 0, y: -56 };

// Fixed page size for the entries table. Kept in sync with
// `PaginationPage`'s hardcoded `itemsPerPage` so the count-based
// `Page X of Y` display matches the number of rows actually fetched
// per request from `GET /entries?offset=&limit=`.
const PAGE_SIZE = 10;

/**
 * Locale-aware number formatter used everywhere counts are rendered in
 * this dialog (Select-all area, toolbar counter, delete confirmation).
 * Anna's UX review asks for thousands separators so numbers like
 * "12,345 of 100,000 selected" read naturally.
 */
const formatCount = (value: number) => value.toLocaleString();

/**
 * Based on the bulk subscription attributes, we create the appropriate column definitions.
 *
 * The non-checkbox columns share the available dialog width equally so
 * they all end up with the same size. The checkbox column keeps its
 * fixed 40px width.
 */
const dynamicColumns: (props: ColumnsProps) => ColumnDef<BulkSubscriptionEntry>[] = ({
    attributes,
    globalSelectMode,
    isSelectionDisabled,
}) => {
    // The DialogContent is sized at 80vw; the DialogBody adds up to
    // 64px of horizontal padding and the checkbox column occupies a
    // fixed 40px. Whatever remains is split evenly across the attribute
    // columns, falling back to the minimum size when the dialog is too
    // narrow to give every column its full share.
    const dialogContentWidth = Math.max(
        0,
        window.innerWidth * 0.8 - DIALOG_HORIZONTAL_PADDING - SELECT_COLUMN_SIZE
    );
    const evenShare = attributes.length > 0
        ? Math.floor(dialogContentWidth / attributes.length)
        : ENTRY_COLUMN_MIN_SIZE;
    const initialSize = Math.max(ENTRY_COLUMN_MIN_SIZE, evenShare);

    const selectColumn: ColumnDef<BulkSubscriptionEntry> = {
        id: 'select',
        size: SELECT_COLUMN_SIZE,
        enableResizing: false,
        meta: {
            cellClassName: 'tw-p-0 tw-h-full tw-text-center',
        },
        header: ({ table }) => {
            const allVisibleSelected = table.getIsAllRowsSelected();
            const someVisibleSelected = table.getIsSomeRowsSelected();
            const isChecked = globalSelectMode || allVisibleSelected;
            return (
                <div className="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10">
                    <Checkbox
                        isChecked={isChecked}
                        isIndeterminate={!isChecked && someVisibleSelected}
                        disabled={globalSelectMode || isSelectionDisabled}
                        onCheckedChange={(value) => {
                            table.getToggleAllRowsSelectedHandler()({ target: { value } });
                        }}
                    />
                </div>
            );
        },
        cell: ({ row, table }) => {
            const isLocked = globalSelectMode || table.getIsAllRowsSelected();
            return (
                <div className="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10">
                    <Checkbox
                        isChecked={row.getIsSelected()}
                        disabled={isLocked || isSelectionDisabled}
                        onCheckedChange={row.getToggleSelectedHandler()}
                    />
                </div>
            );
        },
    };

    const attributeColumns: ColumnDef<BulkSubscriptionEntry>[] = attributes.map(attribute => ({
        id: attribute,
        accessorFn: (row: BulkSubscriptionEntry) => row.entryMap[attribute],
        header: () => <DynamicEntryHeader attributeName={attribute} />,
        size: initialSize,
        minSize: ENTRY_COLUMN_MIN_SIZE,
    }));

    return [selectColumn, ...attributeColumns];
};

const UpdateBulkSubscriptionDialog = ({
    entity,
    open,
    onClose,
}: UpdateBulkSubscriptionDialogProps) => {

    const [currentEntity, setCurrentEntity] = useState(entity);
    // True between "Start import" succeeding and the first list snapshot
    // whose `updatedAt` is newer than the pre-import entity. That snapshot
    // is the server catching up (UPDATING or already READY again).
    const pendingImportStartRef = useRef(false);
    const preImportUpdatedAtRef = useRef(entity.updatedAt);

    // Keep the local snapshot in sync when the parent re-derives the
    // entity from a list refetch (status READY ↔ UPDATING via realtime).
    useEffect(() => {
        if (
            pendingImportStartRef.current &&
            entity.id === currentEntity.id &&
            entity.updatedAt <= preImportUpdatedAtRef.current
        ) {
            setCurrentEntity({ ...entity, status: BulkSubscriptionStatus.UPDATING });
            return;
        }
        pendingImportStartRef.current = false;
        setCurrentEntity(entity);
    }, [entity, currentEntity.id]);

    const isUpdateDisabled = currentEntity.status !== BulkSubscriptionStatus.READY;
    // Figma frame `5887:29535 / 02 State while an import is running`
    // asks for a dedicated info callout while a bulk-sub import is
    // running (status: UPDATING). Other non-READY states (INITIALIZING,
    // PENDING) keep the original warning callout so we don't lose the
    // pre-existing UX for early lifecycle states.
    const isImportRunning = currentEntity.status === BulkSubscriptionStatus.UPDATING;

    const { t } = useTranslation();
    const { toast } = useToast();
    const { user } = useUser();
    const { organization } = useOrganization();
    // Active bulk subscriptions mutate items, so recipients of shared items may only read them
    const isReadonly = isItemNamespaceReadonly(organization) && currentEntity.active;
    const canUpload = !user?.roles?.contains('bulk_subscription_viewer') && !isReadonly;
    const gridRef = useRef<GridRef>(null);
    const dialogContentRef = useRef<HTMLDivElement>(null);
    const gridBlockRef = useRef<HTMLDivElement>(null);
    const prevSelectedCountRef = useRef(0);

    const [rulesTextFilter, setRulesTextFilter] = useState('');
    const [selectedEntries, setSelectedEntries] = useState<BulkSubscriptionEntry[]>([]);
    const [globalSelectMode, setGlobalSelectMode] = useState(false);
    const [toolbarKey, setToolbarKey] = useState(0);
    const [isDeleteSelectionDialogOpen, setIsDeleteSelectionDialogOpen] = useState(false);
    const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [lockedGridHeight, setLockedGridHeight] = useState<number>();
    const [currentPage, setCurrentPage] = useState(1);

    const { mutate: exportEntries } = useExportBulkSubscriptionEntries({
        onError: (error) => {
            logger.error('UpdateBulkSubscriptionDialog: Could not export bulk subscription entries', error);
            toast.error('ERROR_OCCURRED');
        }
    });

    const onDownload = (format: BulkSubDownloadFormat) => {
        exportEntries({
            bulkSubscriptionId: entity.id,
            format: format.toUpperCase() as 'EXCEL' | 'JSON',
        });
    };

    const offset = (currentPage - 1) * PAGE_SIZE;
    const {
        data: bulkSubscriptionEntries,
        isLoading,
        refetch: refetchData,
    } = useBulkSubscriptionEntries({
        variables: {
            bulkSubscriptionId: entity.id,
            offset,
            limit: PAGE_SIZE,
            keywords: rulesTextFilter || undefined,
        },
    });

    // Server-side total (respects `keywords`). We rely on it for
    // "Select all N entries" so we do not have to page through every entry
    // just to know the count.
    const {
        data: totalEntriesCount,
        refetch: refetchEntriesCount,
    } = useBulkSubscriptionEntriesCount({
        variables: {
            bulkSubscriptionId: entity.id,
            keywords: rulesTextFilter || undefined,
        },
    });

    const filteredEntries: BulkSubscriptionEntry[] = useMemo(
        () => bulkSubscriptionEntries ?? [],
        [bulkSubscriptionEntries]
    );

    const filteredCount = totalEntriesCount ?? 0;
    const selectedCount = selectedEntries.length;
    // Figma Frame 02: keep "Select all" visible (but disabled) while an
    // import is running. Other non-READY states still hide it.
    const showSelectAllArea = filteredCount > 0 && (!isUpdateDisabled || isImportRunning);
    const isSelectionDisabled = isImportRunning || isReadonly;
    // Pagination bar shows up as soon as more than one page's worth of
    // filtered entries exist on the server; the local `filteredEntries`
    // array is capped at `PAGE_SIZE` so we cannot derive this from it.
    const shouldShowPaginationFlag = filteredCount > PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(filteredCount / PAGE_SIZE));

    // Measure the Grid block once it has rendered a full first page, then lock
    // that height. Cleared when pagination disappears (≤ 10 entries).
    useLayoutEffect(() => {
        if (!shouldShowPaginationFlag) {
            setLockedGridHeight(undefined);
            return;
        }
        if (lockedGridHeight != null || isLoading) {
            return;
        }

        const height = gridBlockRef.current?.getBoundingClientRect().height ?? 0;
        if (height > 0) {
            setLockedGridHeight(height);
        }
    }, [shouldShowPaginationFlag, lockedGridHeight, isLoading, filteredEntries.length]);
    // `isGlobalSelectMode` is now driven purely by user intent: once the
    // user clicks "Select all N entries" every filtered entry is considered
    // selected server-side (via the `keywords` filter), so we do not require
    // `selectedCount === filteredCount` — with server-side pagination
    // `selectedCount` maxes out at `PAGE_SIZE` and would never catch up
    // to `filteredCount`. This flag gates the column-level "cross-page
    // select-all" behavior (header checkbox disabled, cell checkboxes
    // locked, etc.) and the server-side delete path.
    const isGlobalSelectMode = globalSelectMode;
    // `isEverythingSelected` is the effective visual state used by the
    // top-area "Select all" checkbox and the toolbar labels. In global
    // mode we treat every filtered entry as selected; otherwise we fall
    // back to the "all rows on the current page are ticked and match the
    // total" case (only possible when N <= PAGE_SIZE).
    const isEverythingSelected =
        isGlobalSelectMode ||
        (filteredCount > 0 && selectedCount === filteredCount);

    const columns = useMemo(
        () => dynamicColumns({
            attributes: entity.attributes,
            globalSelectMode: isGlobalSelectMode,
            isSelectionDisabled,
        }),
        [entity.attributes, isGlobalSelectMode, isSelectionDisabled]
    );

    useEffect(() => {
        if (prevSelectedCountRef.current === 0 && selectedCount > 0) {
            setToolbarKey(k => k + 1);
        }

        prevSelectedCountRef.current = selectedCount;
    }, [selectedCount]);

    // Reset to page 1 whenever the keyword filter changes. Without this
    // the user could be sitting on page 5 of the previous result set and
    // see an empty page when the new result set only has two pages.
    useEffect(() => {
        setCurrentPage(1);
    }, [rulesTextFilter]);

    // Clamp `currentPage` when the total shrinks below its start offset
    // (e.g. after deleting entries on the last page). Ensures we never
    // request `offset >= filteredCount`, which would return an empty page.
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    // Keep every row on the current page ticked while global-select mode
    // is on. Because entries are paginated server-side, `filteredEntries`
    // only ever holds one page — this re-applies the "all visible rows
    // selected" state when the user navigates to a new page or when the
    // data is re-fetched (e.g. by the real-time messaging listener).
    useEffect(() => {
        if (!globalSelectMode) return;
        if (filteredEntries.length === 0) return;

        const newSelection: RowSelectionState = Object.fromEntries(
            filteredEntries.map(entry => [entry.combinedValues, true])
        );
        gridRef.current?.setRowSelection(newSelection);
    }, [globalSelectMode, filteredEntries]);

    const refetchEntriesAndCount = useCallback(() => {
        refetchData();
        refetchEntriesCount();
    }, [refetchData, refetchEntriesCount]);

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.BULK_SUBSCRIPTION_CHANGED_EVENT,
        eventHandler: refetchEntriesAndCount,
    });

    const onSelectedRowsChange = useCallback((rows: BulkSubscriptionEntry[]) => {
        setSelectedEntries(rows);
    }, []);

    const clearSelection = () => {
        gridRef.current?.setRowSelection({});
        setGlobalSelectMode(false);
    };

    const toggleSelectAll = () => {
        if (isSelectionDisabled) {
            return;
        }

        if (isEverythingSelected) {
            clearSelection();
            return;
        }

        // Entering global-select mode: mark every visible row on the
        // current page as selected so the header/row checkboxes are in
        // sync visually. The delete flow uses `keywords` server-side
        // (via `DELETE /entries/all`) and does not depend on any
        // client-side selection beyond this visual state.
        const newSelection: RowSelectionState = Object.fromEntries(
            filteredEntries.map(entry => [entry.combinedValues, true])
        );
        gridRef.current?.setRowSelection(newSelection);
        setGlobalSelectMode(true);
    };

    const handleSelectAllAreaKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleSelectAll();
        }
    };

    const handleDeleteSelectionClose = (shouldRefetchData?: boolean) => {
        setIsDeleteSelectionDialogOpen(false);

        if (shouldRefetchData) {
            clearSelection();
            refetchEntriesAndCount();
        }
    };

    const onRenameClose = (shouldRefetchData?: boolean) => {
        if (shouldRefetchData) {
            refetchEntriesAndCount();
        }

        setIsRenameDialogOpen(false);
    };

    const onUploadClose = (shouldRefetchData?: boolean) => {
        if (shouldRefetchData) {
            refetchEntriesAndCount();
            // Import start is accepted before the list refetch / realtime
            // event lands. Flip local status so Upload stays disabled
            // (Figma Frame 02) for the rest of the run.
            pendingImportStartRef.current = true;
            preImportUpdatedAtRef.current = currentEntity.updatedAt;
            setCurrentEntity(prev => ({ ...prev, status: BulkSubscriptionStatus.UPDATING }));
        }

        setIsUploadDialogOpen(false);
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                ref={dialogContentRef}
                variant="large"
                onPointerDownOutside={e => e.preventDefault()}
                onInteractOutside={e => e.preventDefault()}
            >
                <DialogHeader className="!tw-h-auto !tw-px-5 lg:!tw-px-8">
                    <DialogTitle className="tw-sr-only">
                        {t('BULK_SUBSCRIPTION.UPDATE_ENTRIES')}: {entity.name}
                    </DialogTitle>
                    <BulkSubDialogHeader
                        entity={currentEntity}
                        isUploadDisabled={isUpdateDisabled || isImportRunning}
                        isUploadVisible={canUpload}
                        isRenameDisabled={isReadonly}
                        onRename={() => !isReadonly && setIsRenameDialogOpen(true)}
                        onUpload={() => !isImportRunning && !isUpdateDisabled && setIsUploadDialogOpen(true)}
                        onDownload={onDownload}
                    />
                </DialogHeader>
                <DialogBody
                    padding="none"
                    className="tw-flex tw-flex-col tw-min-h-0 tw-min-w-0 tw-h-[80vh] tw-w-full tw-gap-4 tw-overflow-y-auto tw-px-5 tw-py-4 lg:tw-px-8 lg:tw-py-6"
                >
                    {isReadonly && (
                        <Callout
                            canBeClosed={false}
                            variant="info"
                        >
                            {t('BULK_SUBSCRIPTION.ACTIVE_READONLY_FOR_SHARED_ITEMS')}
                        </Callout>
                    )}
                    {isImportRunning && (
                        <Callout
                            canBeClosed={false}
                            variant="info"
                        >
                            {t('BULK_SUBSCRIPTION.IMPORT_RUNNING')}
                        </Callout>
                    )}
                    {isUpdateDisabled && !isImportRunning && (
                        <Callout
                            canBeClosed={false}
                            variant="warning"
                        >
                            {t('BULK_SUBSCRIPTION.UPDATE_ENTRIES_DISABLED_HINT')}
                        </Callout>
                    )}
                    {/*
                      Toolbar stays outside the Grid block. Height is measured from
                      the natural first full page (10 rows + pager), then locked.
                    */}
                    <div className="tw-flex tw-flex-col tw-gap-5 lg:tw-gap-6 tw-min-w-0 tw-w-full tw-min-h-0 tw-flex-1">
                        <EntriesToolbar
                            entity={entity}
                            textFilter={rulesTextFilter}
                            onTextFilterChange={setRulesTextFilter}
                            refetchData={refetchEntriesAndCount}
                        />
                        {showSelectAllArea && (
                            <div
                                className={`tw-flex tw-items-center tw-gap-3 tw-select-none tw-w-fit ${
                                    isSelectionDisabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
                                }`}
                                onClick={isSelectionDisabled ? undefined : toggleSelectAll}
                                onKeyDown={isSelectionDisabled ? undefined : handleSelectAllAreaKeyDown}
                                role="button"
                                tabIndex={isSelectionDisabled ? -1 : 0}
                                aria-disabled={isSelectionDisabled}
                            >
                                <Checkbox
                                    isChecked={isEverythingSelected}
                                    disabled={isSelectionDisabled}
                                    tabIndex={-1}
                                />
                                <span className="tw-text-t-sm lg:tw-text-t-base tw-text-t-primary">
                                    {isEverythingSelected
                                        ? t('BULK_SUBSCRIPTION.CLEAR_SELECTION')
                                        : t('BULK_SUBSCRIPTION.SELECT_ALL_RESULTS', { count: formatCount(filteredCount) })}
                                </span>
                            </div>
                        )}
                        <div
                            ref={gridBlockRef}
                            className={`tw-flex tw-flex-col tw-min-w-0 tw-w-full
                                ${shouldShowPaginationFlag ? 'tw-gap-3' : ''}`}
                            style={lockedGridHeight != null ? { height: lockedGridHeight } : undefined}
                        >
                            <Grid
                                ref={gridRef}
                                // Force every cell / header in the entries table to 40px
                                // per Anna's Figma spec, and strip the default header
                                // padding on the checkbox column so its header checkbox
                                // lines up horizontally with the row checkboxes.
                                className={`tw-min-w-0 tw-w-full tw-flex-1
                                    [&_.table-head]:!tw-h-10 [&_.table-head]:!tw-py-0
                                    [&_.scoped-table-cell]:!tw-h-10 [&_.scoped-table-cell]:!tw-py-0
                                    [&_th[data-column-id='select']]:!tw-p-0
                                    [&_th[data-column-id='select']]:tw-w-10
                                    ${isUpdateDisabled && !shouldShowPaginationFlag ? 'tw-h-[85%]' : ''}`}
                                isLoading={isLoading}
                                columns={columns}
                                onSelectedRowsChange={onSelectedRowsChange}
                                data={filteredEntries}
                                getRowId={row => row.combinedValues}
                                enableRowSelection={false}
                                // Server-side pagination: keep Grid's internal
                                // TanStack pagination off and drive the pager
                                // ourselves via the external `PaginationPage`
                                // below, so we can fetch one page at a time
                                // from `GET /entries?offset=&limit=`.
                                shouldShowPaginationFlag={false}
                            />
                            {shouldShowPaginationFlag && (
                                <PaginationPage
                                    navigation="compact"
                                    totalItems={filteredCount}
                                    currentPage={currentPage}
                                    onPageChange={setCurrentPage}
                                />
                            )}
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter
                    className="tw-bg-fill-primary tw-z-[inherit]"
                >
                    <Button
                        data-testid="button-close-update-dialog"
                        onClick={onClose}
                        variant="secondary"
                    >
                        {t('CLOSE')}
                    </Button>
                </DialogFooter>

                {!isUpdateDisabled && (
                    <ToolbarTable
                        key={toolbarKey}
                        visible={selectedCount > 0}
                        selectedCount={selectedCount}
                        totalCount={filteredCount}
                        allSelected={isEverythingSelected}
                        position="absolute"
                        boundsRef={dialogContentRef}
                        defaultPosition={TOOLBAR_DEFAULT_POSITION}
                        onSelectAll={toggleSelectAll}
                        onDeleteSelected={() => setIsDeleteSelectionDialogOpen(true)}
                        onClose={clearSelection}
                        labels={{
                            selectAll: (total) => isEverythingSelected
                                ? <>{t('BULK_SUBSCRIPTION.CLEAR_SELECTION')}</>
                                : <>{t('BULK_SUBSCRIPTION.SELECT_ALL_ENTRIES', { count: formatCount(total) })}</>,
                            partialSelected: (count, total) => (
                                <>{t('BULK_SUBSCRIPTION.SELECTED_COUNT_OF_TOTAL', { count: formatCount(count), total: formatCount(total) })}</>
                            ),
                            allSelected: (total) => (
                                <>{t('BULK_SUBSCRIPTION.ALL_SELECTED', { total: formatCount(total) })}</>
                            ),
                            delete: t('BULK_SUBSCRIPTION.DELETE_SELECTION'),
                            close: t('CLOSE'),
                        }}
                    />
                )}

                {isDeleteSelectionDialogOpen && (
                    <DeleteSelectedEntriesConfirmationDialog
                        entity={entity}
                        entries={selectedEntries}
                        // When Select-All is active we take the efficient
                        // server-side delete path (DELETE /entries/all with
                        // the current keyword filter) instead of shipping
                        // every entry key in the request body.
                        isGlobalSelection={isGlobalSelectMode}
                        keywords={rulesTextFilter || undefined}
                        totalCount={filteredCount}
                        open={isDeleteSelectionDialogOpen}
                        onClose={handleDeleteSelectionClose}
                    />
                )}
            </DialogContent>

            {
                isRenameDialogOpen &&
                <UpdateDetailsDialog
                    entity={currentEntity}
                    open={isRenameDialogOpen}
                    onClose={onRenameClose}
                    onUpdated={setCurrentEntity}
                />
            }

            {
                isUploadDialogOpen &&
                <UploadNewEntriesDialog
                    entity={entity}
                    open={isUploadDialogOpen}
                    onClose={onUploadClose}
                />
            }
        </Dialog>
    );
};

export { UpdateBulkSubscriptionDialog };
